import { useState } from 'react'
import { Button } from '../../components/Button'
import { Modal } from '../../components/Modal'
import { useAppData } from '../../context/AppDataContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { hasFullVisibility } from '../../hooks/useScopedWork'
import type { Priority, TaskStatus } from '../../data/types'
import { today } from '../../lib/format'
import { Select } from '../../components/Select'
import { DatePicker } from '../../components/DatePicker'

export function NewTaskModal({ open, onClose, defaultStatus = 'Briefed' }: { open: boolean; onClose: () => void; defaultStatus?: TaskStatus }) {
  const { addTask, currentClients, clients, projects, directory } = useAppData()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const canAssignOthers = hasFullVisibility(currentUser?.role)
  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState(currentClients[0]?.id ?? '')
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '')
  // Yours by default; Settings → Preferences can make it start unassigned for
  // whoever is allowed to hand work to somebody else.
  const [assigneeId, setAssigneeId] = useState(
    canAssignOthers && currentUser?.preferences.autoAssign === false
      ? ''
      : currentUser?.linkedPersonaId ?? directory[0]?.id ?? ''
  )
  const [priority, setPriority] = useState<Priority>('Medium')
  // Today, not the fifteenth of September 2026. The design shipped that date
  // hardcoded, so every piece anybody added arrived already overdue — and the
  // board colours it red on a date nobody chose.
  const [dueDate, setDueDate] = useState(today)

  const availableProjects = projects.filter((p) => p.clientId === clientId)

  function reset() {
    setTitle('')
    setPriority('Medium')
    setDueDate(today())
  }

  async function handleCreate() {
    if (!title.trim()) return
    await addTask({
      title: title.trim(),
      description: 'No description provided yet.',
      clientId,
      projectId: projectId || availableProjects[0]?.id || projects[0]?.id,
      // The server ignores/rejects this for Team Member and Viewer accounts
      // and pins it to their own persona regardless — see
      // server/src/routes/data.ts. The picker below is hidden for them so
      // this only ever matters for Admin/Manager.
      assigneeId,
      dueDate,
      priority,
      status: defaultStatus,
      tags: [],
    })
    showToast('Task created successfully.')
    reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New task"
      description="A reel, post or other piece of work, added to a client's month on the board."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!title.trim()}>
            Create task
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="field-label" htmlFor="task-title">
            Title
          </label>
          <input
            id="task-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Design new email header"
            className="field-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label" htmlFor="task-client">
              Client
            </label>
            <Select
              id="task-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="field-input"
            >
              {currentClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="field-label" htmlFor="task-project">
              Project
            </label>
            <Select
              id="task-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="field-input"
            >
              {(availableProjects.length ? availableProjects : projects).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {canAssignOthers && (
            <div>
              <label className="field-label" htmlFor="task-assignee">
                Assignee
              </label>
              <Select
                id="task-assignee"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="field-input"
              >
                <option value="">Unassigned</option>
                {directory.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div>
            <label className="field-label" htmlFor="task-priority">
              Priority
            </label>
            <Select
              id="task-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as Priority)}
              className="field-input"
            >
              {(['Low', 'Medium', 'High', 'Urgent'] as Priority[]).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor="task-due">
            Due date
          </label>
          <DatePicker
            id="task-due"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            placeholder="No due date"
            className="field-input"
          />
        </div>
      </div>
    </Modal>
  )
}
